<template>
  <div class="flex min-h-dvh w-full flex-col bg-bg">
    <!--
      The title, on every page, from the same server meta the blade root renders
      on a cold load. It has to be re-stated here because an Inertia visit
      answers with JSON and never touches the head — without this, a diner who
      walks from Places into a venue and on to the checkout keeps the title of
      whichever page they first landed on.
    -->
    <Head :title="page.props.pageTitle" />

    <!--
      One header, every screen that has chrome at all, at every width.

      A venue used to get a fixed bar instead — bottom of a phone, top of a
      desktop — which meant the app's navigation was a different object and a
      different shape depending on which page you were on and how wide the
      window was. It is one object now, in flow, and it differs only in what it
      names: Places out here, the venue and your table inside one.
    -->
    <PjAppHeader
      v-if="hasNav"
      :context="isVenue ? PJ_APP_CONTEXT.VENUE : PJ_APP_CONTEXT.PJAZZA"
      :has-category-strip="chrome.hasCategoryStrip"
      :live-orders="liveOrders.count"
      :order-context="headerOrderContext"
      :site-slug="isVenue ? site?.slug : undefined"
      :venue-accent="isVenue ? site?.accent : undefined"
      :venue-logo-url="isVenue ? site?.logoUrl : undefined"
      :venue-name="isVenue ? site?.name : undefined"
      width="wide"
      @choose-mode="chooseMode"
      @manage-table="chrome.openTableMenu"
      @open-account="chrome.openAccount"
      @pick-table="chrome.openPicker"
      @scan="chrome.openScan"
    />
    <!--
      No width and no horizontal padding here. Both belong to PjContainer, which
      pages put around their own content: the menu's category strip is a
      full-width band that has to reach the window edge, and it can only do that
      as a sibling of the container rather than a child fighting its way out.

      Vertical space is still the shell's business, because what occupies the top
      and bottom of the screen is the shell's decision.
    -->
    <!-- No room reserved at either end any more: nothing is fixed. The header
         takes its own space at the top, and the only thing floating over the
         foot is the cart bar, which the venue page pads for itself because it
         is the only page that has one. -->
    <main class="w-full flex-1 pb-10">
      <slot />
    </main>

    <slot name="bottom-bar" />

    <!-- Venue chrome sheets rendered globally so they open from the header on any page.

         Loaded once the first screen is up rather than with it. None of them is
         open on arrival, and between them they carry the sign-in, its phone
         parser and the camera, which were most of what a phone downloaded before
         it could draw anything. Each one also renders the moment it is opened,
         so a tap never waits on the idle moment. -->
    <PjAccountSheet
      v-if="isAfterIdle || chrome.isAccountOpen"
      :open="chrome.isAccountOpen"
      @close="chrome.closeAll"
    />

    <PjTableSheet
      v-if="isAfterIdle || chrome.isTableMenuOpen"
      :open="chrome.isTableMenuOpen"
      :tab-href="tabUrl"
      :table-name="orderContext?.tableName"
      @close="chrome.closeAll"
      @leave="leaveTable"
    />

    <PjTablePicker
      v-if="site?.slug && (isAfterIdle || isPickerOpen)"
      v-model:visible="isPickerOpen"
      :site-slug="site.slug"
      @select="chooseTable"
    />

    <PjScanSheet
      v-if="isAfterIdle || chrome.isScanOpen"
      :open="chrome.isScanOpen"
      @close="chrome.closeAll"
    />

    <!-- Teleports to the body, so it rides above every sheet on every screen
         that has chrome at all. -->
    <PjToaster />
  </div>
</template>

<script lang="ts" setup>
import { Head, router, usePage } from '@inertiajs/vue3'
import { computed, defineAsyncComponent, watch } from 'vue'

import type { PjazzaPickableTable } from '@/domains/pjazza/components/molecules/pj-table-picker/PjTablePicker.types.ts'
import type { PjAppContext } from '@/domains/pjazza/types/consts/AppContext.consts.ts'
import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import type { OrderSharedProps } from '@/domains/pjazza/types/pages.types.ts'

import PjAppHeader from '@/domains/pjazza/components/organisms/pj-app-header/PjAppHeader.vue'
import PjToaster from '@/domains/pjazza/components/organisms/pj-toaster/PjToaster.vue'
import { useAfterIdle } from '@/domains/pjazza/composables/use-after-idle/useAfterIdle.ts'
import { JOIN_ACTION, useOrderContext } from '@/domains/pjazza/composables/use-order-context/useOrderContext.ts'
import { useLiveOrdersStore } from '@/domains/pjazza/store/use-live-orders-store/useLiveOrdersStore.ts'
import { useOrderModeStore } from '@/domains/pjazza/store/use-order-mode-store/useOrderModeStore.ts'
import { useTabStore } from '@/domains/pjazza/store/use-tab-store/useTabStore.ts'
import { useVenueChromeStore } from '@/domains/pjazza/store/use-venue-chrome-store/useVenueChromeStore.ts'
import { PJ_APP_CONTEXT } from '@/domains/pjazza/types/consts/AppContext.consts.ts'
import { ORDER_MODE } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'

/**
 * The shell.
 *
 * Two contexts, one header. In Pjazza it offers Places; in a venue it names the
 * venue and offers the table you are sitting at. Both carry the wordmark and
 * both reach the diner's own account, which is the change ARCH-2026-011 records:
 * inside a venue there was previously nothing of ours reachable without leaving
 * the page you were ordering from.
 *
 * The scanner is bare — full-screen camera, no chrome of any kind.
 */
const props = withDefaults(
  defineProps<{
    /** 'pjazza' and 'venue' both show the header; 'bare' shows nothing. */
    context?: PjAppContext
    /** False where the page is not about ordering, so the header states no mode. */
    hasOrderMode?: boolean
  }>(),
  { context: undefined, hasOrderMode: true }
)

// Each in a chunk of its own, off the path to the first screen. See the template.
const PjAccountSheet = defineAsyncComponent(
  () => import('@/domains/pjazza/components/organisms/pj-account-sheet/PjAccountSheet.vue')
)
const PjScanSheet = defineAsyncComponent(
  () => import('@/domains/pjazza/components/organisms/pj-scan-sheet/PjScanSheet.vue')
)
const PjTablePicker = defineAsyncComponent(
  () => import('@/domains/pjazza/components/molecules/pj-table-picker/PjTablePicker.vue')
)
const PjTableSheet = defineAsyncComponent(
  () => import('@/domains/pjazza/components/organisms/pj-table-sheet/PjTableSheet.vue')
)

// The venue's shared props, which is where the bar's table state comes from —
// the same object the page reads, so the two cannot disagree about which venue
// is on screen.
const page = usePage<OrderSharedProps>()

const liveOrders = useLiveOrdersStore()

const tabStore = useTabStore()

const chrome = useVenueChromeStore()

const isAfterIdle = useAfterIdle()

const context = computed(() => props.context ?? PJ_APP_CONTEXT.PJAZZA)

const isVenue = computed(() => context.value === PJ_APP_CONTEXT.VENUE)

const hasNav = computed(() => context.value !== PJ_APP_CONTEXT.BARE)

const site = computed(() => page.props.site ?? null)

const orderMode = useOrderModeStore()

// The same object the menu page reads, resolved once. It used to be a predicate
// written here and a near-copy written there, and the two disagreed.
const orderContext = useOrderContext()

const headerOrderContext = computed(() => (isVenue.value && props.hasOrderMode ? orderContext.value : null))

/**
 * A waiter-session venue has no table to switch the mode to — dine-in there
 * is a printed code, not a state modeFor() can just declare — so choosing it
 * opens the scanner instead of leaving the store's choice to be silently
 * discarded until the diner is holding a table it can apply to.
 */
const chooseMode = (next: OrderMode): void => {
  orderMode.choose(next)

  if (next === ORDER_MODE.DINE_IN && orderContext.value?.join === JOIN_ACTION.SCAN) {
    chrome.openScan()
  }
}

const tabUrl = computed(() => (site.value?.slug ? route('pjazza.tab', { site: site.value.slug }) : ''))

const isPickerOpen = computed({
  get: () => chrome.isPickerOpen,
  set: (val: boolean) => {
    if (!val) chrome.closeAll()
  }
})

const leaveTable = (): void => {
  chrome.closeAll()
  tabStore.leave()
  if (site.value?.slug) {
    router.visit(route('pjazza.menu', { site: site.value.slug }))
  }
}

const chooseTable = (table: PjazzaPickableTable): void => {
  if (site.value?.slug) {
    tabStore.choose({ siteSlug: site.value.slug, tableId: table.id, tableName: table.name })
  }
  chrome.closeAll()
}

// A sheet left open in a store would reopen itself on the next venue.
watch(context, chrome.closeAll)
</script>
